import {
  Box,
  ButtonGroup,
  DialogActions,
  DialogContent,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  styled,
} from '@mui/material';
import { DialogDefault, ThemeButton, ThemeTypography } from 'DesignSystem';
import useDatasetMutation from 'FeatureModules/DataImport/hooks/useDatasetMutation';
import {
  EGeoDataExportFormat,
  EGeoDataExportType,
  TGeoDataExportFormat,
  TGeoDataExportType,
} from 'graphql/secure/mutations/types/dataset-mutation.types';
import useMessages from 'hooks/useMessages';
import React, { FC, useEffect, useState } from 'react';
import { IDefaultDialogProps } from 'types/dialog.types';
import { downloadFile, downloadFileWithBlob } from 'utils/download.utils';

interface IDownloadGeoDataDialogProps extends IDefaultDialogProps {
  geoDataId: string;
  /**
   * If true, the dialog only allows downloading the validation file.
   */
  validationOnly?: boolean;
  disableGeojson?: boolean;
}

const Container = styled(Box)(({ theme }) => ({
  display: 'flex',
  padding: theme.spacing(2),
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '8px',
  borderRadius: '6px',
  background: theme.custom.themeColors.primary[5],
  marginBottom: theme.spacing(2),
}));

const StyledFormControlLabel = styled(FormControlLabel)(({ theme }) => ({
  '&.MuiFormControlLabel-root': {
    alignItems: 'flex-start',
  },
}));

const FormatSelection: FC<{
  format: TGeoDataExportFormat;
  options?: TGeoDataExportFormat[];
  onChange: (format: TGeoDataExportFormat) => void;
}> = ({
  format,
  options = [EGeoDataExportFormat.GEOJSON, EGeoDataExportFormat.CSV, EGeoDataExportFormat.EXCEL],
  onChange,
}) => (
  <Box display="flex" mt={1} flexDirection="column" gap={1}>
    <ThemeTypography variant="BODY_MEDIUM_BOLD">
      Select which file format to download.
    </ThemeTypography>
    <ButtonGroup>
      {options.includes(EGeoDataExportFormat.GEOJSON) && (
        <ThemeButton
          color="WHITE"
          className={format === EGeoDataExportFormat.GEOJSON ? 'selected' : ''}
          onClick={() => onChange(EGeoDataExportFormat.GEOJSON)}
          typographyColor="ALMOST_BLACK"
        >
          .geojson
        </ThemeButton>
      )}
      {options.includes(EGeoDataExportFormat.EXCEL) && (
        <ThemeButton
          color="WHITE"
          className={format === EGeoDataExportFormat.EXCEL ? 'selected' : ''}
          onClick={() => onChange(EGeoDataExportFormat.EXCEL)}
          typographyColor="ALMOST_BLACK"
        >
          .xlsx
        </ThemeButton>
      )}
      {options.includes(EGeoDataExportFormat.CSV) && (
        <ThemeButton
          color="WHITE"
          className={format === EGeoDataExportFormat.CSV ? 'selected' : ''}
          onClick={() => onChange(EGeoDataExportFormat.CSV)}
          typographyColor="ALMOST_BLACK"
        >
          .csv
        </ThemeButton>
      )}
    </ButtonGroup>
    <ThemeTypography variant="BODY_MEDIUM">
      For geojson, all data points and any potential issues are included as properties for each
      plot, but might not always be accessible when opened, depending on the file manager.
    </ThemeTypography>
  </Box>
);

const DownloadGeoDataDialog: FC<IDownloadGeoDataDialogProps> = ({
  open,
  geoDataId,
  validationOnly,
  disableGeojson,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [exportType, setExportType] = useState<TGeoDataExportType>(
    validationOnly ? EGeoDataExportType.VALIDATED : EGeoDataExportType.VERIFIED
  );
  const [exportFormat, setExportFormat] = useState<TGeoDataExportFormat>(
    disableGeojson ? EGeoDataExportFormat.EXCEL : EGeoDataExportFormat.GEOJSON
  );

  const { setErrorMessage } = useMessages();
  const { createGeoEntitiesExport } = useDatasetMutation();

  const handleExport = async () => {
    setLoading(true);
    try {
      const response = await createGeoEntitiesExport({
        geoDataInput: { id: geoDataId, type: exportType },
        exportFormat,
      });
      const fileUrl = response?.data?.createGeoEntitiesExport?.fileUrl;
      if (fileUrl) {
        if (exportFormat === EGeoDataExportFormat.GEOJSON) {
          const urlParts = fileUrl.split('/');
          const filename = urlParts[urlParts.length - 1] || 'geo-data.geojson';
          await downloadFileWithBlob(fileUrl, filename);
        } else {
          await downloadFile(fileUrl);
        }
      } else {
        throw new Error('Could not export the dataset');
      }
    } catch (error) {
      setErrorMessage('Could not export the dataset. Please try again or contact the support');
    } finally {
      setLoading(false);
      onClose?.();
    }
  };

  useEffect(() => {
    if (
      exportType === EGeoDataExportType.VERIFIED &&
      exportFormat !== EGeoDataExportFormat.GEOJSON
    ) {
      setExportFormat(EGeoDataExportFormat.GEOJSON); // For verified datasets we only support GEOJSON format
    }
  }, [exportType, exportFormat]);

  return (
    <DialogDefault open={open} iconName="download" title="Download geo-data" onClose={onClose}>
      <DialogContent>
        <Container>
          <ThemeTypography variant="BODY_MEDIUM_BOLD">
            Select a version of the geo-dataset to download
          </ThemeTypography>
          <RadioGroup
            value={exportType}
            onChange={e => setExportType(e.target.value as TGeoDataExportType)}
          >
            <StyledFormControlLabel
              value="validated"
              control={<Radio value={EGeoDataExportType.VALIDATED} size="small" />}
              label={
                <>
                  <ThemeTypography variant="BODY_MEDIUM_BOLD">Validated dataset</ThemeTypography>
                  <ThemeTypography variant="BODY_MEDIUM">
                    Initially imported geo-dataset including potential highlighted data formatting
                    errors.
                  </ThemeTypography>

                  {exportType === EGeoDataExportType.VALIDATED && (
                    <FormatSelection
                      format={exportFormat}
                      options={
                        disableGeojson
                          ? [EGeoDataExportFormat.EXCEL, EGeoDataExportFormat.CSV]
                          : undefined
                      }
                      onChange={setExportFormat}
                    />
                  )}
                </>
              }
            />

            {!validationOnly && (
              <>
                <Box my={2}>
                  <Divider />
                </Box>
                <StyledFormControlLabel
                  value="verified"
                  control={<Radio value={EGeoDataExportType.VERIFIED} size="small" />}
                  label={
                    <>
                      <ThemeTypography variant="BODY_MEDIUM_BOLD">Verified dataset</ThemeTypography>
                      <ThemeTypography variant="BODY_MEDIUM">
                        Most recent status of the geo-data, including all assigned IDs, included
                        data points and any potential verification warnings about this dataset’s
                        completeness or formatting issues.
                      </ThemeTypography>

                      {exportType === EGeoDataExportType.VERIFIED && !disableGeojson && (
                        <FormatSelection
                          format={exportFormat}
                          options={[EGeoDataExportFormat.GEOJSON]}
                          onChange={setExportFormat}
                        />
                      )}
                    </>
                  }
                />
              </>
            )}
          </RadioGroup>
        </Container>

        <ThemeTypography variant="BODY_MEDIUM">
          You can share this geo-data with external partners and address any potential issues or
          missing details.
        </ThemeTypography>
      </DialogContent>

      <DialogActions>
        <Box display="flex" justifyContent="space-between" width="100%">
          <ThemeButton color="BLUE_ICE" onClick={onClose}>
            Cancel
          </ThemeButton>
          <ThemeButton loading={loading} onClick={handleExport}>
            Download
          </ThemeButton>
        </Box>
      </DialogActions>
    </DialogDefault>
  );
};

export default DownloadGeoDataDialog;
